/**
 * @file repPricingView.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The office's Rep Pricing view (Cam, 28 Oct 2026 — the Pricing
 *              & Costing redesign's D3 "A second view inside Pricing &
 *              Costing", D4 "Rep first", D5 "By delivery, NO reminders", D9,
 *              D11). Pure rules for components/RepPricingView.js over the
 *              server's summary (GET /api/pricing-costing/rep-pricing, rules
 *              utils/repPricing.js):
 *
 *   filterRequests / filterFlags   the Lines view's filters carried over
 *                    (search every word, reps by name with "No rep", product
 *                    categories by sales account).
 *   railOf           reps down the left with their numbers, worked out from
 *                    the filtered requests — "All reps" first, "No rep on the
 *                    account" last.
 *   dueSections      To send by delivery: already delivered (red) first, then
 *                    the next 7 days, then later, then no date. No reminders.
 *   waitingBySend    Waiting grouped by the send its lines went in, oldest
 *                    first.
 *   documentSummary  one document in the Dealt with picker as a clear short
 *                    summary (Cam: "otherwise they will have to pick from
 *                    memory").
 */

import { matchesAllTerms } from './searchMatch';
import { productCategoryOf } from './pricingCosting';

export const REP_ALL = 'all';
export const REP_NONE = 'none';
// The Lines view's "No rep" value in its rep filter (PricingCosting NO_REP).
export const NO_REP_FILTER = '__none__';

export const TABS = [
  { key: 'send', label: 'To send', icon: 'outgoing_mail' },
  { key: 'waiting', label: 'Waiting', icon: 'schedule_send' },
  { key: 'priced', label: 'Priced by rep', icon: 'price_check' },
  { key: 'flagged', label: 'Flagged by rep', icon: 'flag' },
  { key: 'sends', label: 'Sends', icon: 'outbox' }
];

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const time = (v) => {
  const t = v ? new Date(v).getTime() : NaN;
  return Number.isFinite(t) ? t : null;
};

/** A rep's key in the list: 'all', 'none' (no rep on the account) or the rep id as text. */
export const repKeyOf = (repId) => (repId ? String(repId) : REP_NONE);

/** Does a request (or flag) belong to the chosen rep? */
export function forRep(repKey, repId) {
  if (!repKey || repKey === REP_ALL) return true;
  if (repKey === REP_NONE) return !repId;
  return String(repId || '') === String(repKey);
}

/** Does a request pass the carried-over filters? */
function requestPasses(q, { search = '', reps = [], categories = [] } = {}) {
  if (reps.length && !reps.includes(q.rep_name || NO_REP_FILTER)) return false;
  if (categories.length && !categories.includes(productCategoryOf(q.product && q.product.range))) return false;
  if (search && !matchesAllTerms([
    q.customer && q.customer.name, q.customer && q.customer.account_no,
    q.product && q.product.code, q.product && q.product.name, ...(q.additives || []),
    ...(q.orders || []).map(o => o.order_number), ...(q.suppliers || []), q.rep_name
  ], search)) return false;
  return true;
}

/** The requests the carried-over filters keep. */
export function filterRequests(requests, filters = {}) {
  return (requests || []).filter(q => requestPasses(q, filters));
}

/** The flags the carried-over filters keep (no product category on a flag — searched instead). */
export function filterFlags(flags, { search = '', reps = [] } = {}) {
  return (flags || []).filter(f => {
    if (reps.length && !reps.includes(f.rep_name || NO_REP_FILTER)) return false;
    if (search && !matchesAllTerms([
      f.line && f.line.customer && f.line.customer.name, f.line && f.line.customer && f.line.customer.account_no,
      f.line && f.line.product && f.line.product.code, f.line && f.line.product && f.line.product.name,
      f.line && f.line.order_number, f.invoice && f.invoice.invoice_no, f.note, f.rep_name
    ], search)) return false;
    return true;
  });
}

/** The requests for a contract the carried-over filters keep. */
export function filterContractAsks(asks, filters = {}) {
  return (asks || []).filter(a => requestPasses({
    ...a, orders: (a.lines || []).map(l => ({ order_number: l.order_number }))
  }, filters));
}

const blank = () => ({
  to_send: { requests: 0, lines: 0, delivered: 0 },
  waiting: { requests: 0, lines: 0, since: null },
  priced: { requests: 0, lines: 0, held: 0 },
  flagged: 0, contract: 0
});

/**
 * The rep list (D4): every rep the summary lists (and any rep a request or
 * flag names), each with its numbers worked out from what the filters keep.
 * "All reps" first, "No rep on the account" last; a rep filter in the Lines
 * view narrows the list to those reps.
 * @returns {Array<{ key, rep_id, name, email, last_send, counts }>}
 */
export function railOf({ reps = [], requests = [], flags = [], contractAsks = [], filters = {} }) {
  const byKey = new Map();
  const entry = (repId, name, email = null, lastSend = null) => {
    const key = repKeyOf(repId);
    if (!byKey.has(key)) byKey.set(key, { key, rep_id: repId || null, name: repId ? (name || 'Rep') : 'No rep on the account', email, last_send: lastSend, counts: blank() });
    const e = byKey.get(key);
    if (!e.email && email) e.email = email;
    if (!e.last_send && lastSend) e.last_send = lastSend;
    return e;
  };
  for (const r of reps) entry(r.rep_id, r.rep_name, r.rep_email, r.last_send);
  const all = { key: REP_ALL, rep_id: null, name: 'All reps', email: null, last_send: null, counts: blank() };
  const add = (e, fn) => { fn(e.counts); fn(all.counts); };
  for (const q of requests) {
    const e = entry(q.rep_id, q.rep_name);
    const lines = (q.line_ids || []).length;
    if (q.bucket === 'to_send') {
      add(e, c => { c.to_send.requests += 1; c.to_send.lines += lines; if (q.delivered) c.to_send.delivered += 1; });
    } else if (q.bucket === 'waiting') {
      add(e, c => {
        c.waiting.requests += 1; c.waiting.lines += lines;
        if (q.sent_at && (!c.waiting.since || time(q.sent_at) < time(c.waiting.since))) c.waiting.since = q.sent_at;
      });
    } else if (q.bucket === 'priced_by_rep') {
      add(e, c => { c.priced.requests += 1; c.priced.lines += lines; if (q.held) c.priced.held += 1; });
    }
  }
  for (const f of flags) add(entry(f.rep_id, f.rep_name), c => { c.flagged += 1; });
  for (const a of contractAsks) add(entry(a.rep_id, a.rep_name), c => { c.contract += 1; });

  const repFilter = filters.reps || [];
  const list = [...byKey.values()].filter(e => {
    if (!repFilter.length) return true;
    return repFilter.includes(e.rep_id ? e.name : NO_REP_FILTER);
  });
  // A rep with nothing at all, no rep, is left out when empty.
  const shown = list.filter(e => e.rep_id || hasAnything(e.counts));
  shown.sort((a, b) => {
    if (!a.rep_id !== !b.rep_id) return a.rep_id ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
  return [all, ...shown];
}

export const hasAnything = (c) => !!(c.to_send.requests || c.waiting.requests || c.priced.requests || c.flagged || c.contract);

/** Tab counts for the chosen rep. */
export function tabCounts(entry) {
  const c = (entry && entry.counts) || blank();
  return { send: c.to_send.requests, waiting: c.waiting.requests, priced: c.priced.requests + c.contract, flagged: c.flagged, sends: null };
}

/** 'YYYY-MM-DD' plus n days. */
export function addDays(day, n) {
  const d = new Date(`${String(day).slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * To send, by delivery (D5): already delivered and still not priced (red)
 * first, then delivering in the next 7 days, then later, then no date. Within
 * each, soonest first (the server's order). Empty sections are left out.
 */
export function dueSections(requests, today) {
  const week = today ? addDays(today, 7) : null;
  const sections = [
    { key: 'delivered', label: 'Delivered, no price yet', icon: 'event_busy', tone: 'err', requests: [] },
    { key: 'week', label: 'Delivering in the next 7 days', icon: 'date_range', tone: '', requests: [] },
    { key: 'later', label: 'Delivering later', icon: 'event_upcoming', tone: '', requests: [] },
    { key: 'nodate', label: 'No delivery date', icon: 'event', tone: '', requests: [] }
  ];
  for (const q of requests || []) {
    const d = q.earliest_delivery;
    const k = q.delivered ? 'delivered' : !d ? 'nodate' : (week && d <= week ? 'week' : 'later');
    sections.find(s => s.key === k).requests.push(q);
  }
  return sections.filter(s => s.requests.length);
}

/**
 * Waiting, grouped by the send its lines went in (a recorded send, or one
 * from the email log), oldest first; lines asked before any send was kept
 * by the day they were asked.
 */
export function waitingBySend(requests) {
  const groups = new Map();
  for (const q of requests || []) {
    const s = q.send || null;
    const key = s && s.send_id ? `send:${s.send_id}` : `asked:${String(q.sent_at || '').slice(0, 10) || 'unknown'}`;
    if (!groups.has(key)) groups.set(key, { key, send: s && s.send_id ? s : null, at: (s && s.at) || q.sent_at || null, by: s && s.by, requests: [], lines: 0 });
    const g = groups.get(key);
    g.requests.push(q);
    g.lines += (q.line_ids || []).length;
    if (!g.at && q.sent_at) g.at = q.sent_at;
  }
  return [...groups.values()].sort((a, b) => (time(a.at) ?? Infinity) - (time(b.at) ?? Infinity));
}

/** The lines of these requests. */
export const lineIdsOf = (requests) => [...new Set((requests || []).flatMap(q => q.line_ids || []))];

/** The costs { line id: cost } the send window splits by (costs.view only — none otherwise). */
export function costsOf(requests) {
  const out = {};
  for (const q of requests || []) Object.assign(out, q.costs || {});
  return out;
}

const DOC_LABELS = {
  invoice: 'Invoice', credit_note: 'Credit note', debit_note: 'Debit note', overpayment: 'Overpayment',
  prepayment: 'Prepayment', order: 'Order', contract: 'Contract'
};
const RELATION_LABELS = {
  this_line: "This line's",
  raised_for_this_line: "Raised for this line's invoice",
  since_flag: 'Since the flag'
};

const gbp = (v) => {
  const n = num(v);
  if (n === null) return null;
  return `${n < 0 ? '−' : ''}£${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const ddmmyyyy = (d) => {
  const s = String(d || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : null;
};
const qty = (v) => {
  const n = num(v);
  if (n === null) return '';
  return ` × ${Number.isInteger(n) ? n : Math.round(n * 100) / 100}`;
};

/**
 * One document in the Dealt with picker, as a clear short summary:
 *   title     "Credit note CN-D00136"
 *   meta      "28/10/2026 · £36.00 · £36.00 outstanding · for INV-0101068 · from SO-D10233"
 *   products  "Price correction × 3; Haulage × 1 · and 1 more"
 *   relation  "Raised for this line's invoice" (or null)
 *   searchText  every word of it, for the picker's search
 */
export function documentSummary(d) {
  const label = DOC_LABELS[d.type] || 'Document';
  const title = d.type === 'contract' ? String(d.ref || '') : `${label} ${d.ref || ''}`.trim();
  const meta = [];
  if (d.type === 'contract') {
    if (d.period) meta.push(d.period);
    if (d.date || d.end_date) meta.push(`${ddmmyyyy(d.date) || '…'} – ${ddmmyyyy(d.end_date) || '…'}`);
    if (num(d.price) !== null) meta.push(`price ${gbp(d.price)}`);
    if (d.state && d.state !== 'live') meta.push(d.state === 'on_hold' ? 'on hold' : d.state);
  } else {
    if (d.date) meta.push(d.type === 'order' ? `delivery ${ddmmyyyy(d.date)}` : ddmmyyyy(d.date));
    if (num(d.total) !== null) meta.push(gbp(d.total));
    if (d.type !== 'order' && num(d.outstanding) !== null) {
      const credit = ['credit_note', 'overpayment', 'prepayment'].includes(d.type);
      meta.push(num(d.outstanding) === 0 ? (credit ? 'all used' : 'paid') : `${gbp(d.outstanding)} ${credit ? 'left' : 'outstanding'}`);
    }
    if (d.for_invoice) meta.push(`for ${d.for_invoice}`);
    if (d.order_number && d.type !== 'order') meta.push(`from ${d.order_number}`);
  }
  const productList = (d.products || []).map(p => `${p.description || p.code || 'Line'}${qty(p.quantity)}`);
  const products = productList.length
    ? `${productList.join('; ')}${d.more_lines ? ` · and ${d.more_lines} more` : ''}`
    : (d.type === 'order' || d.type === 'contract' ? '' : 'No lines');
  return {
    title,
    meta: meta.join(' · '),
    products,
    relation: RELATION_LABELS[d.relation] || null,
    searchText: [title, meta.join(' '), products, d.reference || '', d.order_number || ''].join(' ')
  };
}

/** The value a document has in the picker. */
export const documentValue = (d) => `${d.type}:${d.id}`;

/** The document a picker value names, from the list. */
export const documentOf = (value, docs) => (docs || []).find(d => documentValue(d) === value) || null;

/** "Dealt with: CN-D00136 — credited £10/T" for a flag that was. */
export function resolvedText(flag) {
  const r = flag && flag.resolved;
  if (!r) return null;
  const parts = [r.resolution === 'withdrawn' ? 'Withdrawn by the rep' : 'Dealt with'];
  if (r.by) parts[0] += ` by ${r.by}`;
  if (r.at) parts[0] += ` on ${ddmmyyyy(new Date(r.at).toISOString())}`;
  if (r.document) parts.push(`settled by ${r.document.ref || DOC_LABELS[r.document.type] || 'a document'}`);
  if (r.note) parts.push(`"${r.note}"`);
  return parts.join(' — ');
}
